import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { classNames } from "primereact/utils";
import React, { useRef, useState } from "react";
import Http from "../../../Common/Http";

const Create = ({ setVisible, fetchData, projects, onSuccess }) => {
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [inputs, setInputs] = useState({
    name: "",
    goal: "",
    start_date: null,
    end_date: null,
    status: "Planning",
    project_id: null,
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (e, field) => {
    setInputs((prev) => ({ ...prev, [field]: e.value }));
  };

  const handleDropdownChange = (e, field) => {
    setInputs((prev) => ({ ...prev, [field]: e.value }));
  };

  const statusOptions = [
    { label: "Planning", value: "Planning" },
    { label: "Active", value: "Active" },
    { label: "Completed", value: "Completed" },
  ];

  const formatDateForSubmit = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    // Validate inputs
    if (!inputs.name || !inputs.start_date || !inputs.end_date || !inputs.status) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please fill in all required fields.",
        life: 3000,
      });
      return;
    }

    // Validate end date is after start date
    if (new Date(inputs.end_date) < new Date(inputs.start_date)) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "End date must be after start date.",
        life: 3000,
      });
      return;
    }

    setLoading(true);

    try {
      const response = await Http.post("/sprint", {
        name: inputs.name,
        goal: inputs.goal,
        start_date: formatDateForSubmit(inputs.start_date),
        end_date: formatDateForSubmit(inputs.end_date),
        status: inputs.status,
        project_id: inputs.project_id,
      });

      if (response.status === 201) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Sprint created successfully!",
          life: 3000,
        });

        if (typeof fetchData === "function") {
          fetchData();
        }

        if (typeof onSuccess === "function") {
          onSuccess(response.data.data);
        }

        setVisible(false);
      }
    } catch (error) {
      console.error("Error creating sprint:", error);
      const errorMessage = error.response?.data?.message || "An error occurred while creating the sprint.";
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: errorMessage,
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Toast ref={toast} />
      <form onSubmit={handleSubmit} className="p-fluid">
        <div className="p-field mb-3">
          <label htmlFor="name" className="font-bold">
            Sprint Name *
          </label>
          <InputText
            id="name"
            name="name"
            value={inputs.name}
            onChange={handleChange}
            className={classNames({ "p-invalid": submitted && !inputs.name })}
            placeholder="Enter sprint name"
          />
          {submitted && !inputs.name && <small className="p-error">Sprint name is required.</small>}
        </div>

        <div className="p-field mb-3">
          <label htmlFor="project_id" className="font-bold">
            Project
          </label>
          <Dropdown
            id="project_id"
            name="project_id"
            value={inputs.project_id}
            options={projects.map((project) => ({ label: project.title || project.name, value: project.id }))}
            onChange={(e) => handleDropdownChange(e, "project_id")}
            placeholder="Select a project"
            className="w-100"
          />
        </div>

        <div className="p-field mb-3">
          <label htmlFor="goal" className="font-bold">
            Goal
          </label>
          <InputTextarea id="goal" name="goal" value={inputs.goal} onChange={handleChange} rows={3} placeholder="Enter sprint goal" />
        </div>

        <div className="grid">
          <div className="col-6 p-field mb-3">
            <label htmlFor="start_date" className="font-bold">
              Start Date *
            </label>
            <Calendar
              id="start_date"
              name="start_date"
              value={inputs.start_date}
              onChange={(e) => handleDateChange(e, "start_date")}
              showIcon
              className={classNames({ "p-invalid": submitted && !inputs.start_date })}
              dateFormat="yy-mm-dd"
            />
            {submitted && !inputs.start_date && <small className="p-error">Start date is required.</small>}
          </div>

          <div className="col-6 p-field mb-3">
            <label htmlFor="end_date" className="font-bold">
              End Date *
            </label>
            <Calendar
              id="end_date"
              name="end_date"
              value={inputs.end_date}
              onChange={(e) => handleDateChange(e, "end_date")}
              showIcon
              className={classNames({ "p-invalid": submitted && !inputs.end_date })}
              dateFormat="yy-mm-dd"
            />
            {submitted && !inputs.end_date && <small className="p-error">End date is required.</small>}
          </div>
        </div>

        <div className="p-field mb-3">
          <label htmlFor="status" className="font-bold">
            Status *
          </label>
          <Dropdown
            id="status"
            name="status"
            value={inputs.status}
            options={statusOptions}
            onChange={(e) => handleDropdownChange(e, "status")}
            className={classNames({ "p-invalid": submitted && !inputs.status })}
          />
          {submitted && !inputs.status && <small className="p-error">Status is required.</small>}
        </div>

        <div className="d-flex justify-content-end">
          <Button label="Cancel" icon="pi pi-times" className="p-button-text mr-2" onClick={() => setVisible(false)} type="button" />
          <Button label="Create" icon="pi pi-check" className="p-button-success" type="submit" loading={loading} />
        </div>
      </form>
    </div>
  );
};

export default Create;
